import { IconButton, Typography } from "@mui/material";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { Column, PatientSubscriptionsWithPatient } from "src/api/types";
import { Paths } from "src/constants/paths";
import QuizIcon from "@mui/icons-material/Quiz";
import { useViewIntakeDrawer } from "src/context/ViewIntakeProvider/UseViewIntakes";

export const useColumns = (): Column<PatientSubscriptionsWithPatient>[] => {
  const { handleOpenViewIntake } = useViewIntakeDrawer();

  return [
    {
      id: "patient_id",
      label: "Patient ID",
      minWidth: 20,
      align: "center",
      orderable: true,
      orderBy: "patient_id",
      format: (row) => row.patient_id.toString(),
      render: (row) => (
        <Link
          to={`${Paths.DASHBOARD_PATIENTS}/${row.patient_id}`}
          target="_blank"
        >
          {row.patient_id}
        </Link>
      ),
    },
    {
      id: "id",
      label: "Subscription ID",
      minWidth: 20,
      align: "center",
      orderable: true,
      orderBy: "id",
      format: (row) => row.id.toString(),
      render: (row) => <Typography>{row.id}</Typography>,
    },
    {
      id: "display_name",
      label: "Subscription",
      minWidth: 100,
      align: "center",
      orderable: false,
      format: (row) => row.display_name,
      render: (row) => <Typography>{row.display_name}</Typography>,
    },

    {
      id: "patient_name",
      label: "Patient",
      minWidth: 90,
      align: "center",
      orderable: false,
      format: (row) =>
        `${row.patient.profile.first_name ?? "N/A"} ${
          row.patient.profile.last_name ?? "N/A"
        }`,
      render: (row) => (
        <Typography>{`${row.patient.profile.first_name ?? "N/A"} ${
          row.patient.profile.last_name ?? "N/A"
        }`}</Typography>
      ),
    },
    {
      id: "region",
      label: "State",
      minWidth: 50,
      align: "center",
      orderable: false,
      format: (row) =>
        row.patient.profile.region != null ? row.patient.profile.region : "N/A",
      render: (row) => {
        if (row.patient.profile.region == null) {
          return "N/A";
        }
        return <Typography>{row.patient.profile.region}</Typography>;
      },
    },

    {
      id: "cancellation_requested_at",
      label: "Cancellation requested at",
      minWidth: 60,
      align: "center",
      orderable: true,
      orderBy: "cancellation_requested_at",
      format: (subscription) =>
        subscription.cancellation_requested_at
          ? format(
              new Date(subscription.cancellation_requested_at || ""),
              "MM/dd/yyyy",
            )
          : "N/A",

      render: (row) => {
        if (row.cancellation_requested_at == null) {
          return "N/A";
        }

        return (
          <Typography>
            {format(
              new Date(row.cancellation_requested_at || ""),
              "MM/dd/yyyy",
            )}
          </Typography>
        );
      },
    },
    {
      id: "canceled_at",
      label: "Canceled at",
      minWidth: 60,
      align: "center",
      orderable: true,
      orderBy: "canceled_at",
      format: (row) => row.canceled_at ?? "N/A",
      render: (row) => {
        if (row.canceled_at == null) {
          return "N/A";
        }

        return (
          <Typography>
            {format(new Date(row.canceled_at || ""), "MM/dd/yyyy")}
          </Typography>
        );
      },
    },
    {
      id: "intake",
      label: "View intake",
      minWidth: 40,
      align: "center",
      orderable: false,
      format: (row) => row.id.toString(),
      render: (row) => (
        <IconButton
          size="small"
          onClick={() => handleOpenViewIntake(row)}
          color="secondary"
        >
          <QuizIcon />
        </IconButton>
      ),
    },
  ];
};
